import type {
  GuestRequest,
  LoginRequest,
  RegisterRequest,
  UpdatePasswordByHashRequest,
} from '@scayle/storefront-nuxt'
import { FetchError } from 'ofetch'
import type { Ref } from 'vue'
import { computed, onUnmounted, readonly, ref } from 'vue'
import { useState } from 'nuxt/app'
import { clearNuxtData } from '#app/composables/asyncData'
import { useRouteHelpers, useToast, useTrackingEvents } from '~/composables'
import type { AuthenticationType, AuthTrackingEvent } from '~/types/tracking'
import { useI18n } from '#i18n'
import { useRoute } from '#app/composables/router'
import {
  useBasket,
  useLog,
  useRpcCall,
  useSession,
  useUser,
  useWishlist,
} from '#storefront/composables'
import { routeList } from '~/utils'
import { useApplyPromotions } from '#storefront-promotions/composables/useApplyPromotions'
import { useMurId } from '~/composables/useMurId'
import { useDefaultBreakpoints } from '#storefront-ui'

interface UseAuthenticationReturn {
  /** An async function that handles the authentication process for a regular user */
  login: (data: Omit<LoginRequest, 'shop_id'>) => Promise<void>
  /** An async function that handles the authentication process for a guest user */
  guestLogin: (data: Omit<GuestRequest, 'shop_id'>) => Promise<void>
  /** An async function that handles logging out the user */
  logout: () => Promise<void>
  /** An async function that handles registering the user */
  register: (data: Omit<RegisterRequest, 'shop_id'>) => Promise<void>
  /** An async function that handles the password recovery process for a specific user */
  forgotPassword: (email: string) => Promise<boolean>
  /** An async function that handles resetting the password using a hash */
  resetPasswordByHash: (
    data: Omit<UpdatePasswordByHashRequest, 'shop_id'>,
  ) => Promise<void>
  /** An async function that handles login through an identity provider (IDP) */
  loginIDP: (code: string) => Promise<void>
  /** A readonly reactive boolean that indicates whether each of the actions mentioned above is currently in the process of being submitted */
  isSubmitting: Readonly<Ref<boolean, boolean>>
  /** A readonly reactive string for storing the error message */
  errorMessage: Readonly<Ref<string | null, string | null>>
  /** A function that cleares the error message */
  clearErrorMessage: () => void
}

export default UseAuthenticationReturn

/**
 * A composable for authentication actions and data manipulation.
 * In addition of interacting with the authentication, it also takes care of tracking,
 * handling errors and displaying success toast messages.

 * @param event - Authentication event type
 * @param options
 * @returns An {@link UseAuthenticationReturn} object containing reactive authentication data and functions.
 */
export function useAuthentication(
  event: AuthTrackingEvent,
  options: {
    method?: AuthenticationType
    intent?: string
  } = {
    method: 'email',
    intent: 'login',
  },
): UseAuthenticationReturn {
  const { method, intent } = options
  const $i18n = useI18n()
  const route = useRoute()
  const { isGreater } = useDefaultBreakpoints()
  const errorMessage = useState<string | null>(event, () => null)

  const toast = useToast()

  const { trackLoginError } = useTrackingEvents()
  const { trackAuthenticated, trackSignUp, trackLogout } = useTrackingEvents()

  const session = useSession()
  const { localizedNavigateTo } = useRouteHelpers()
  const log = useLog('useAuthentication')

  const isSubmitting = ref(false)
  const successMessage = computed<string>(() => {
    return $i18n.t(`authentication.notification.success.${event}`)
  })

  const { refresh: refreshWishlist } = useWishlist()
  const { refresh: refreshBasket, data: basketData } = useBasket()
  const { user, refresh: refreshUser, customerType } = useUser()
  const { applyPromotions } = useApplyPromotions({ basket: basketData })

  const refresh = async (): Promise<void> => {
    await Promise.all([refreshUser(), refreshWishlist(), refreshBasket()])
    // After the a user logs in the "logged out" basket gets merged with the users basket.
    // The users basket could contain items where a promotion can be applied.
    await applyPromotions()
  }

  const login = async (data: Omit<LoginRequest, 'shop_id'>): Promise<void> => {
    isSubmitting.value = true

    try {
      await session.login(data)

      // Merge baskets if necessary from old implementation to new one with user.referenceKey
      const basketKeys = useRpcCall('getBasketKeys')
      const { toBasketKey, fromBasketKey } = await basketKeys()
      if (fromBasketKey && toBasketKey && fromBasketKey !== toBasketKey) {
        const mergeBaskets = useRpcCall('mergeBaskets')
        await mergeBaskets({
          fromBasketKey,
          toBasketKey,
        })
        await refreshBasket()
      }

      await authenticated()
    } catch (error: unknown) {
      await trackLoginError({
        status: 'error',
        category: 'account',
        method: 'email',
        action: 'login',
      })
      handleError(error)
    }

    isSubmitting.value = false
  }

  const loginIDP = async (code: string): Promise<void> => {
    isSubmitting.value = true

    try {
      await session.loginWithIDP({ code })
      await authenticated()
    } catch (error) {
      handleError(error)
    }

    isSubmitting.value = false
  }

  const guestLogin = async (
    data: Omit<GuestRequest, 'shop_id'>,
  ): Promise<void> => {
    isSubmitting.value = true

    try {
      await session.guestLogin(data)
      await authenticated()
    } catch (error) {
      trackFailedAuthentication(data.email)
      handleError(error)
    }

    isSubmitting.value = false
  }

  const register = async (
    data: Omit<RegisterRequest, 'shop_id'>,
  ): Promise<void> => {
    isSubmitting.value = true

    try {
      await session.register(data)
      await authenticated()
    } catch (error) {
      trackFailedAuthentication(data.email)
      handleError(error)
    }

    isSubmitting.value = false
  }

  const forgotPassword = async (email: string): Promise<boolean> => {
    let hasSuccess = true

    isSubmitting.value = true

    try {
      await session.forgetPassword({ email })
      toast.show(successMessage.value, { action: 'CONFIRM', type: 'INFO' })
    } catch (error) {
      handleError(error)
      hasSuccess = false
    }
    isSubmitting.value = false

    return hasSuccess
  }

  const resetPasswordByHash = async (
    data: Omit<UpdatePasswordByHashRequest, 'shop_id'>,
  ): Promise<void> => {
    isSubmitting.value = true

    try {
      await session.resetPasswordByHash(data)
      await authenticated()
    } catch (error) {
      handleError(error)
    }

    isSubmitting.value = false
  }

  const logout = async (): Promise<void> => {
    isSubmitting.value = true

    try {
      await session.revokeToken()
      // we call `useUser` in templates/nuxt/middleware/authGuard.global.ts with the key `authGuard-user`.
      // We need to delete the user data for this key on logout to reset the state in the auth guard.
      // Without deleting the date, the auth guard would allow navigating to protected pages for logged out users.
      clearNuxtData('authGuard-user')
      if (user.value) {
        trackLogout()
      }
    } catch (error) {
      handleError(error)
    }

    await refresh()

    isSubmitting.value = false

    redirectUser(routeList.home.path)
  }

  const clearErrorMessage = (): void => {
    errorMessage.value = null
  }

  /**
   * After a user was authenticated by login in, or registering.
   * Refresh user data, basket & wishlist.
   */
  const authenticated = async (): Promise<void> => {
    await refresh()

    if (!user.value) {
      return
    }

    if (event === 'sign_up') {
      const { murId } = useMurId({ user: user.value })

      await trackSignUp(
        {
          event,
          method,
          intent,
          status: 'successful',
          customer_id: user.value.id,
          mur_id: murId.value,
          login: user.value,
        },
        user.value.email,
      )
    } else {
      await trackAuthenticated(
        {
          event,
          method,
          status: 'successful',
          customer_id: user.value.id,
          customer_type: customerType.value,
        },
        user.value.email,
      )
    }

    await redirectUser(
      (route.query.redirectUrl as string) ?? routeList.home.path,
    )
    if (!user.value.status?.isGuestCustomer && isGreater('md')) {
      toast.show(successMessage.value, { action: 'CONFIRM', type: 'SUCCESS' })
    }
  }

  const trackFailedAuthentication = async (email: string): Promise<void> => {
    const { murId } = useMurId({ user: user.value })

    await trackAuthenticated(
      {
        event,
        method,
        mur_id: murId.value,
        status: 'error',
      },
      email,
    )
  }

  const getGenericErrorMessage = (status: number): string => {
    switch (status) {
      case 400:
        return $i18n.t('authentication.notification.error.generic.400')
      case 401:
        return $i18n.t('authentication.notification.error.generic.401')
      case 403:
        return $i18n.t('authentication.notification.error.generic.403')
      case 404:
        return $i18n.t('authentication.notification.error.generic.404')
      case 406:
        return $i18n.t('authentication.notification.error.generic.406')
      case 409:
        return $i18n.t('authentication.notification.error.generic.409')
      case 422:
        return $i18n.t('authentication.notification.error.generic.422')
      case 500:
        return $i18n.t('authentication.notification.error.generic.500')
      default:
        return $i18n.t('authentication.notification.error.generic.500')
    }
  }

  const handleError = (error: unknown): void => {
    if (error instanceof FetchError) {
      const status = error.response?.status
      if (status) {
        const authFlowSpecificPath = `authentication.notification.error.${event}.${status}`
        // Prioritize specific translations for certain flows (e.g., guest login error 409).
        // If a specific translation is not available, fall back to the general sign-in error message translations.
        errorMessage.value = $i18n.te(authFlowSpecificPath)
          ? $i18n.t(authFlowSpecificPath)
          : getGenericErrorMessage(status)
      }
    }
    // remove user data (email, password) from the error object, before logging it

    // @ts-expect-error Property 'config' does not exist on type '{}'.ts(2339)
    delete error?.config?.data

    log.error(`Error while ${event} was called.`, {
      error,
    })
  }

  const redirectUser = async (redirectTo: string) => {
    return route.fullPath === redirectTo
      ? window.location.reload()
      : await localizedNavigateTo(redirectTo)
  }

  onUnmounted(() => clearErrorMessage())

  return {
    login,
    guestLogin,
    logout,
    register,
    forgotPassword,
    resetPasswordByHash,
    loginIDP,
    clearErrorMessage,
    isSubmitting: readonly(isSubmitting),
    errorMessage: readonly(errorMessage),
  }
}
